import React, { createContext, useState, useEffect, ReactNode, useCallback, useContext } from 'react';
import { Tenant } from '../models/tenant';
import { AuthContext } from './authContext';
import { fetchTenant, getCachedTenant } from '../services/tenantService';

/**
 * Interface for Tenant Context state and actions.
 */
interface TenantContextType {
  tenant: Tenant | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export const TenantContext = createContext<TenantContextType | undefined>(undefined);

/**
 * Props for TenantProvider component.
 */
interface TenantProviderProps {
  children: ReactNode;
}

/**
 * TenantProvider component manages tenant state across the application.
 * Provides centralized tenant data management with refresh capabilities.
 * 
 * All components needing tenant data should use useTenant hook from this provider.
 * 
 * @param {TenantProviderProps} props - Component props containing children
 */
export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenant, setTenant] = useState<Tenant | null>(() => getCachedTenant());
  const [isLoading, setIsLoading] = useState<boolean>(!getCachedTenant());
  const [error, setError] = useState<string | null>(null);

  const authContext = useContext(AuthContext);
  const isLoggedIn = authContext?.isLoggedIn || false;

  /**
   * Fetches tenant data from the API and updates the context state.
   * Bypasses cache when explicitly refreshing (e.g., after settings update).
   */
  const refresh = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const tenantData = await fetchTenant(false); // false = bypass cache
      setTenant(tenantData);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch tenant data';
      setError(errorMessage);
      setTenant(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Initialize tenant data on mount when user is logged in.
   */
  useEffect(() => {
    const initTenant = async (): Promise<void> => {
      if (!isLoggedIn) {
        setTenant(null);
        setIsLoading(false);
        return;
      }

      try {
        const tenantData = await fetchTenant();
        setTenant(tenantData);
        setError(null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch tenant data';
        setError(errorMessage);
        setTenant(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (!tenant && isLoggedIn) {
      initTenant();
    } else if (!isLoggedIn) {
      setTenant(null);
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  }, [isLoggedIn]);

  const value: TenantContextType = {
    tenant,
    isLoading,
    error,
    refresh,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

/**
 * Custom hook to access tenant context.
 * Must be used within a component that is a descendant of TenantProvider.
 * 
 * @returns {TenantContextType} Tenant context with tenant data and refresh method
 */
export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};
